"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
type ScaleStageProps = {
  width: number;
  height: number;
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
  /**
   * Extra room outside the artboard, in artboard px, for content that is meant
   * to escape (a dragged card, a soft shadow). Scaled with the stage so the
   * allowance is identical at every viewport width. Still a hard clip, so
   * nothing can leak far enough to give the document a horizontal scrollbar.
   */
  bleed?: number;
};

/**
 * Fixed Figma artboard scaled to fill its container width.
 * Uses ResizeObserver (not cqi) — cq units fall back to the viewport when the
 * containment context is flaky, which zooms the artboard and clips it.
 */
export function ScaleStage(t0) {
  const $ = _c(16);
  const {
    width,
    height,
    children,
    className,
    style,
    bleed: t1
  } = t0;
  const bleed = t1 === undefined ? 0 : t1;
  const ref = useRef(null);
  const [scale, setScale] = useState(0);
  let t2;
  let t3;
  if ($[0] !== width) {
    t2 = () => {
      const el = ref.current;
      if (!el) {
        return;
      }
      const measure = () => {
        const next = el.clientWidth / width;
        setScale(Number.isFinite(next) && next > 0 ? next : 1);
      };
      measure();
      const ro = new ResizeObserver(measure);
      ro.observe(el);
      return () => ro.disconnect();
    };
    t3 = [width];
    $[0] = width;
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  useEffect(t2, t3);
  const t4 = bleed > 0 ? "clip" : "hidden";
  const t5 = bleed > 0 ? `${bleed * scale}px` : undefined;
  let t6;
  if ($[3] !== style || $[4] !== t4 || $[5] !== t5) {
    t6 = {
      position: "absolute",
      inset: 0,
      overflow: t4,
      overflowClipMargin: t5,
      ...style
    };
    $[3] = style;
    $[4] = t4;
    $[5] = t5;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== children || $[8] !== height || $[9] !== scale || $[10] !== width) {
    t7 = scale > 0 ? <div style={{
      position: "absolute",
      left: 0,
      top: 0,
      width,
      height,
      transform: `scale(${scale})`,
      transformOrigin: "top left",
      pointerEvents: "none"
    }}>{children}</div> : null;
    $[7] = children;
    $[8] = height;
    $[9] = scale;
    $[10] = width;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== className || $[13] !== t6 || $[14] !== t7) {
    t8 = <div ref={ref} className={className} style={t6}>{t7}</div>;
    $[12] = className;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  return t8;
}